import { useCallback, useEffect, useMemo, useState } from "react";

import { isCategoryBlocked } from "@/lib/isCategoryBlocked";
import { useQueryClient } from "@tanstack/react-query";

import { showToast } from "@/components/ui";
import {
  useTaxonomyCategoriesSearchQuery,
  useTaxonomyCategoriesSuggestQuery,
  useTaxonomySaveProductTypeMappingMutation,
} from "@/graphql/generated";

import type {
  CategorySuggestion,
  ProductType,
} from "./useCanonicalCategoryMapping.types";

const DEFAULT_MARKETPLACE = "AMAZON";
const MAX_SUGGESTIONS = 8;

function getGraphqlErrorMessage(error: unknown): string | undefined {
  const err = error as { response?: { errors?: Array<{ message?: unknown }> } };
  const message = err.response?.errors?.[0]?.message;
  return typeof message === "string" ? message : undefined;
}

export function useCanonicalCategoryMapping(
  selectedProductType: ProductType | null,
  onSave?: (canonicalCategoryId: string) => void,
  marketplace?: string,
  sourceQuery?: string | null,
  initialCategory?: CategorySuggestion | null,
) {
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] =
    useState<CategorySuggestion | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    setSelectedCategory(initialCategory ?? null);
    setSearchQuery("");
  }, [initialCategory, selectedProductType]);

  const suggestQueryText =
    selectedProductType?.displayName?.trim() ||
    selectedProductType?.name?.trim() ||
    "";

  const hints = useMemo(() => {
    const collected: string[] = [];
    if (sourceQuery?.trim()) collected.push(sourceQuery.trim());
    if (selectedProductType?.name) collected.push(selectedProductType.name);
    if (selectedProductType?.displayName)
      collected.push(selectedProductType.displayName);
    return collected.length > 0 ? collected : undefined;
  }, [selectedProductType, sourceQuery]);

  const suggestQuery = useTaxonomyCategoriesSuggestQuery(
    {
      q: suggestQueryText,
      hints,
      limit: MAX_SUGGESTIONS,
      marketplace: marketplace ?? DEFAULT_MARKETPLACE,
      productType: selectedProductType?.name,
    },
    { enabled: suggestQueryText.length > 0 },
  );

  const isSearchMode = searchQuery.trim().length >= 2;

  const searchQueryResult = useTaxonomyCategoriesSearchQuery(
    { q: searchQuery.trim() },
    { enabled: isSearchMode },
  );

  const allSuggestions = useMemo((): CategorySuggestion[] => {
    const items = suggestQuery.data?.taxonomyCategoriesSuggest ?? [];
    return items.slice(0, MAX_SUGGESTIONS).map((item) => ({
      categoryId: item.categoryPath,
      path: item.categoryPath,
      confidence: item.score ?? 0,
      reason: "Semantic match",
      mappedTo: item.mappedTo ?? null,
    }));
  }, [suggestQuery.data?.taxonomyCategoriesSuggest]);

  const searchResults = useMemo((): CategorySuggestion[] => {
    const items = searchQueryResult.data?.taxonomyCategoriesSearch ?? [];
    return items.map((item) => ({
      categoryId: item.path,
      path: item.path,
      confidence: item.score ?? 0,
      reason: "Search result",
    }));
  }, [searchQueryResult.data]);

  const saveMutation = useTaxonomySaveProductTypeMappingMutation();

  const handleSelectSuggestion = useCallback(
    (suggestion: CategorySuggestion) => {
      if (isCategoryBlocked(suggestion)) return;
      setSelectedCategory(suggestion);
    },
    [],
  );

  const handleClearSelection = useCallback(() => {
    setSelectedCategory(null);
  }, []);

  const handleConfirmMapping = useCallback(() => {
    if (!selectedProductType || !selectedCategory) return;

    saveMutation.mutate(
      {
        input: {
          marketplace: marketplace ?? DEFAULT_MARKETPLACE,
          productType: selectedProductType.name,
          marketplaceCategoryId: selectedProductType.categoryId,
          canonicalCategoryId: selectedCategory.path,
        },
      },
      {
        onSuccess: () => {
          setSelectedCategory(null);
          queryClient.invalidateQueries({
            queryKey: ["TaxonomyResolutionTasks"],
          });
          showToast.success(
            "Mapping Saved Successfully",
            `${selectedProductType.displayName} → ${selectedCategory.path}`,
          );
          onSave?.(selectedCategory.path);
        },
        onError: (err) => {
          showToast.error(
            "Save Failed",
            getGraphqlErrorMessage(err) ??
              (err instanceof Error
                ? err.message
                : "Could not save canonical mapping"),
          );
        },
      },
    );
  }, [
    selectedProductType,
    selectedCategory,
    saveMutation,
    queryClient,
    onSave,
    marketplace,
  ]);

  return {
    selectedCategory,
    searchQuery,
    setSearchQuery,
    isSearchMode,
    allSuggestions,
    searchResults,
    isSearching: searchQueryResult.isFetching,
    suggestionsLoading: suggestQuery.isLoading,
    suggestQueryText,
    saveMutation,
    handleSelectSuggestion,
    handleClearSelection,
    handleConfirmMapping,
  };
}
